import './FirstPage.scss'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import axiosIct from '../../utils/axios'
import { useModal } from '../../hooks/useModal'
import Modal from '../../components/Modal'
import styled from 'styled-components'
import { FormikErrors, useFormik } from 'formik'
import Input from '../../components/Input/Input'
import DragAndDropFile from '../../components/Input/DragAndDropFile'
import { RoutesEnum } from '../../core/enums'
import { Company } from '../../core/types'
import { ReactComponent as CompanyLogo } from '../../icons/global.svg'
import { Add } from '@mui/icons-material'
import { SketchPicker } from 'react-color'

export default function FirstPage(): React.ReactElement {
  const [clientList, setClientList] = useState<Company[]>([])
  const [selectedColor, setSelectedColor] = useState<string>('#' + Math.floor(Math.random() * 16777215).toString(16)); // Random initial color

  // Function to generate a random color
  const generateRandomColor = () => {
    const randomColor = '#' + Math.floor(Math.random() * 16777215).toString(16);
    return randomColor;
  }

  const {
    isModalOpened: isClientModalOpened,
    closeModal: closeClientModal,
    openModal: openClientModel
  } = useModal()

  const fetchClients = async (): Promise<void> => {
    try {
      const response = await axiosIct.get<Company[]>('/clients/')
      setClientList(response.data.map(client => ({
        ...client,
        clientColor: client.client_color || generateRandomColor()  // Assign random color if not available
      })))
    } catch (error: any) {
      toast.error('Error fetching clients', { theme: 'dark' })
    }
  }

  useEffect(() => {
    fetchClients()
  }, [])

  const clientFormik = useFormik<{ clientName: string; clientLogo: File | null; clientColor: string }>({
    initialValues: {
      clientName: '',
      clientLogo: null,
      clientColor: selectedColor
    },
    validate: (values) => {
      const errors: FormikErrors<{ clientName: string; clientLogo: File | null; clientColor: string }> = {}
      if (!values.clientName.trim()) {
        errors.clientName = 'Required'
      }
      return errors
    },
    onSubmit: async (values) => {
      const formData = new FormData()
      formData.append('company_name', values.clientName)
      if (values.clientLogo) {
        formData.append('logo', values.clientLogo)
      }
      formData.append('clientColor', values.clientColor)

      try {
        await axiosIct.post('/clients/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        toast.success('Client added successfully!', { theme: 'dark' })
        closeClientModal()
        await fetchClients()
      } catch (error: any) {
        toast.error(error.message, { theme: 'dark' })
      }
    }
  })

  return (
    <>
      <div className="admin-dashboard d-flex flex-column">
        <div className="header-spacing"></div>
        <div className="dashboard-header d-flex align-items-baseline justify-content-between">
          <h3 className="overview-bold text--orange">All Clients</h3>
          <button
            className="btn--add-client"
            onClick={ () =>  { setSelectedColor(generateRandomColor()); openClientModel()} }
          >
            Add New
            <Add className='icon--add-client' style={{ color: '#ffffff' }}/>
          </button>
        </div>

        {clientList.length === 0 && (
          <div className="dashboard_no_result">No Clients</div>
        )}

        <div className="content-container">
          {clientList.map((c, index) => (
            <div
              key={index}
              className="client-card"
              onClick={() =>
                window.location.href = `${RoutesEnum.Opportunities}?Client=${encodeURIComponent(c.company_id)}`
              }
            >
              <div
                className="logo-wrapper"
                style={{ backgroundColor: c.client_color || generateRandomColor() }} // Apply color to the logo wrapper
              >
                {c.client_logo_url && 
                  <img src={c.client_logo_url} alt={`${c.company_name} Logo`} className="client-logo" />
                }
              </div>
              <h1 className="client-name-heading">{c.company_name}</h1>
            </div>
          ))}
        </div>
      </div>

      <Modal
        isOpened={isClientModalOpened}
        className="clientModal"
        closeModal={closeClientModal}
      >
        <div className="modal-content">
          <ModalTitle className="fw-bold m text--dark">Add New Client</ModalTitle>
          <p className="text-justify mt-2 client-modal-text">Client / Company Name.</p>
          <form className="w-100 d-flex flex-column" onSubmit={clientFormik.handleSubmit}>
            <Input
              onBlur={clientFormik.handleBlur}
              value={clientFormik.values.clientName}
              onChange={clientFormik.handleChange}
              name="clientName"
              className="mb-4 light clientModalInput"
              required
              style={{ fontSize: '22px' }}
              error={clientFormik.touched.clientName && clientFormik.errors.clientName}
            />
            <label className="Input__label">Select Color</label>
            <div className="color-picker-container">
              <div className="color-box" style={{ backgroundColor: clientFormik.values.clientColor }}/>
              <SketchPicker
                className='sketch-picker'
                color={clientFormik.values.clientColor} // Sync the selected color with Formik state
                onChangeComplete={(color) => {
                  // Update Formik's field value when the color is selected
                  clientFormik.setFieldValue('clientColor', color.hex);
                }}
              />
            </div>
            <label className="Input__label">Client Logo (Optional)</label>
            <DragAndDropFile
              onFileSelect={(file: File | null) => clientFormik.setFieldValue('clientLogo', file)}
            />
            <button className=" save-btn" type="submit">
              Add
            </button>
          </form>
        </div>
      </Modal>
    </>
  )
}

const ModalTitle = styled.div`
  font-size: 32px;
  line-height: 48px;
  color: #072136;
`

